(() => {
  const elementName = 'gh-navigator-hotspots';

  if (customElements.get(elementName)) return;

  class GhNavigatorHotspots extends HTMLElement {
    constructor() {
      super();

      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handlePointerEnter = this.handlePointerEnter.bind(this);
      this.handlePointerLeave = this.handlePointerLeave.bind(this);
      this.handleFocus = this.handleFocus.bind(this);
      this.handleBlur = this.handleBlur.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleImageLoad = this.handleImageLoad.bind(this);
      this.handleImageError = this.handleImageError.bind(this);
      this.handleVideoReady = this.handleVideoReady.bind(this);
      this.handleVideoError = this.handleVideoError.bind(this);
      this.updateConnector = this.updateConnector.bind(this);
    }

    connectedCallback() {
      this.stage = this.querySelector('[data-gh-hotspot-stage]');
      this.preview = this.querySelector('[data-gh-hotspot-preview]');
      this.previewImage = this.querySelector('[data-gh-hotspot-preview-image]');
      this.previewVideos = [...this.querySelectorAll('[data-gh-hotspot-preview-video]')];
      this.connector = this.querySelector('[data-gh-hotspot-connector]');
      this.connectorLine = this.querySelector('[data-gh-hotspot-connector-line]');
      this.status = this.querySelector('[data-gh-hotspot-status]');
      this.buttons = [...this.querySelectorAll('[data-gh-hotspot-button]')];
      this.canHover = window.matchMedia?.('(hover: hover)').matches ?? false;

      if (!this.stage || !this.buttons.length) return;

      this.buttons.forEach((button) => {
        button.addEventListener('click', this.handleClick);
        button.addEventListener('keydown', this.handleKeydown);
        button.addEventListener('pointerenter', this.handlePointerEnter);
        button.addEventListener('pointerleave', this.handlePointerLeave);
        button.addEventListener('focus', this.handleFocus);
        button.addEventListener('blur', this.handleBlur);
      });

      this.previewImage?.addEventListener('load', this.handleImageLoad);
      this.previewImage?.addEventListener('error', this.handleImageError);
      this.bindPreviewVideos();

      this.addEventListener('shopify:block:select', this.handleBlockSelect);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.updateConnector);
        this.resizeObserver.observe(this.stage);
        if (this.preview) this.resizeObserver.observe(this.preview);
      } else {
        window.addEventListener('resize', this.updateConnector);
      }

      this.hidePreview();
      this.warmVideos();
    }

    disconnectedCallback() {
      this.buttons?.forEach((button) => {
        button.removeEventListener('click', this.handleClick);
        button.removeEventListener('keydown', this.handleKeydown);
        button.removeEventListener('pointerenter', this.handlePointerEnter);
        button.removeEventListener('pointerleave', this.handlePointerLeave);
        button.removeEventListener('focus', this.handleFocus);
        button.removeEventListener('blur', this.handleBlur);
      });

      this.previewImage?.removeEventListener('load', this.handleImageLoad);
      this.previewImage?.removeEventListener('error', this.handleImageError);
      this.unbindPreviewVideos();
      this.removeEventListener('shopify:block:select', this.handleBlockSelect);
      this.resizeObserver?.disconnect();
      window.removeEventListener('resize', this.updateConnector);
    }

    handleClick(event) {
      if (!this.canHover) this.activate(event.currentTarget, false);
    }

    handlePointerEnter(event) {
      if (this.canHover) this.activate(event.currentTarget, false);
    }

    handlePointerLeave(event) {
      if (this.canHover && this.activeButton === event.currentTarget) this.hidePreview();
    }

    handleFocus(event) {
      this.activate(event.currentTarget, false);
    }

    handleBlur(event) {
      if (this.activeButton === event.currentTarget && !event.currentTarget.matches(':hover')) {
        this.hidePreview();
      }
    }

    handleKeydown(event) {
      const currentIndex = this.buttons.indexOf(event.currentTarget);
      if (currentIndex < 0) return;

      const isRtl = getComputedStyle(this).direction === 'rtl';
      let nextIndex = null;

      switch (event.key) {
        case 'ArrowLeft':
          nextIndex = currentIndex + (isRtl ? 1 : -1);
          break;
        case 'ArrowRight':
          nextIndex = currentIndex + (isRtl ? -1 : 1);
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = this.buttons.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      nextIndex = (nextIndex + this.buttons.length) % this.buttons.length;
      this.activate(this.buttons[nextIndex], true);
    }

    handleBlockSelect(event) {
      const blockId = event.detail?.blockId;
      const selectedButton = blockId
        ? this.buttons.find((button) => button.dataset.blockId === blockId)
        : event.target.closest?.('[data-gh-hotspot-button]');

      if (selectedButton) this.activate(selectedButton, false);
    }

    activate(button, moveFocus) {
      if (!button) return;

      this.activeButton = button;
      this.buttons.forEach((candidate) => {
        const isActive = candidate === button;
        candidate.setAttribute('aria-selected', String(isActive));
        candidate.tabIndex = isActive ? 0 : -1;
      });

      const mediaUrl = button.dataset.gifUrl;
      const label = button.dataset.label || button.getAttribute('aria-label') || 'Technology feature';

      if (this.preview && mediaUrl) {
        this.preview.classList.add('is-visible');
        this.preview.setAttribute('aria-labelledby', button.id);
        this.preview.setAttribute('aria-hidden', 'false');
        this.preview.removeAttribute('data-error');
        this.showPreviewMedia(button, mediaUrl, label);
      } else if (this.preview) {
        this.hidePreview();
        return;
      }

      if (this.status) this.status.textContent = `${label} selected`;
      if (moveFocus) button.focus();

      requestAnimationFrame(this.updateConnector);
    }

    hidePreview() {
      this.activeButton = null;
      this.buttons?.forEach((button) => button.setAttribute('aria-selected', 'false'));

      if (this.preview) {
        this.preview.classList.remove('is-visible');
        this.preview.setAttribute('aria-hidden', 'true');
        this.preview.removeAttribute('aria-labelledby');
      }

      if (this.previewImage) {
        this.previewImage.hidden = true;
        this.previewImage.removeAttribute('src');
        this.previewImage.alt = '';
      }

      this.pausePreviewVideos();

      if (this.connector) this.connector.hidden = true;
      if (this.status) this.status.textContent = '';
    }

    isVideoMedia(button, mediaUrl) {
      if (button?.dataset.mediaType === 'video') return true;
      return /\.mp4(\?|$)/i.test(mediaUrl || '') || /\/videos\//i.test(mediaUrl || '');
    }

    bindPreviewVideos() {
      this.previewVideos.forEach((video) => {
        video.addEventListener('loadeddata', this.handleVideoReady);
        video.addEventListener('canplay', this.handleVideoReady);
        video.addEventListener('error', this.handleVideoError);
      });
    }

    unbindPreviewVideos() {
      this.previewVideos?.forEach((video) => {
        video.removeEventListener('loadeddata', this.handleVideoReady);
        video.removeEventListener('canplay', this.handleVideoReady);
        video.removeEventListener('error', this.handleVideoError);
      });
    }

    warmVideos() {
      this.previewVideos.forEach((video) => {
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.preload = 'auto';
        video.controls = false;
        if (video.readyState === 0 && video.networkState !== 2) video.load();
      });
    }

    showPreviewMedia(button, mediaUrl, label) {
      if (this.isVideoMedia(button, mediaUrl)) {
        this.showPreviewVideo(button, label);
        return;
      }

      this.pausePreviewVideos();
      if (this.previewImage) this.previewImage.hidden = false;
      const shouldReplaceImage = this.previewImage?.getAttribute('src') !== mediaUrl;
      if (shouldReplaceImage) this.replacePreviewImage(mediaUrl, label);
    }

    showPreviewVideo(button, label) {
      if (this.previewImage) {
        this.previewImage.hidden = true;
        this.previewImage.removeAttribute('src');
        this.previewImage.alt = '';
      }

      this.previewVideos.forEach((video) => {
        const isActive = video.dataset.blockId === button.dataset.blockId;
        video.classList.toggle('is-active', isActive);
        video.controls = false;
        video.muted = true;
        video.defaultMuted = true;
        video.loop = true;
        video.playsInline = true;
        video.removeAttribute('controls');

        if (!isActive) {
          video.pause();
          return;
        }

        video.setAttribute('aria-label', label);

        const startPlayback = () => {
          const playPromise = video.play();
          if (playPromise?.catch) {
            playPromise.catch((error) => {
              if (error?.name !== 'AbortError') this.handleVideoError({ currentTarget: video });
            });
          }
        };

        if (video.currentTime > 0.05 && video.readyState >= 2) {
          video.addEventListener('seeked', startPlayback, { once: true });
          try {
            video.currentTime = 0;
          } catch (error) {
            startPlayback();
          }
        } else {
          startPlayback();
        }
      });
    }

    pausePreviewVideos() {
      this.previewVideos?.forEach((video) => {
        video.classList.remove('is-active');
        video.pause();
      });
    }

    replacePreviewImage(gifUrl, label) {
      const currentImage = this.previewImage;
      const nextImage = currentImage.cloneNode(false);

      currentImage.removeEventListener('load', this.handleImageLoad);
      currentImage.removeEventListener('error', this.handleImageError);

      nextImage.removeAttribute('src');
      nextImage.hidden = false;
      nextImage.alt = label;
      nextImage.loading = 'eager';
      nextImage.fetchPriority = 'high';
      nextImage.addEventListener('load', this.handleImageLoad);
      nextImage.addEventListener('error', this.handleImageError);
      nextImage.src = gifUrl;

      currentImage.replaceWith(nextImage);
      this.previewImage = nextImage;

      if (nextImage.complete) {
        if (nextImage.naturalWidth) {
          this.handleImageLoad();
        } else {
          this.handleImageError();
        }
      }
    }

    handleImageLoad() {
      this.preview?.removeAttribute('data-error');
      this.updateConnector();
    }

    handleImageError() {
      this.preview?.setAttribute('data-error', 'true');
      if (this.status) this.status.textContent = 'Animation could not be loaded';
    }

    handleVideoReady(event) {
      const video = event.currentTarget;
      if (!video?.classList.contains('is-active') || !this.preview?.classList.contains('is-visible')) return;

      this.preview.removeAttribute('data-error');
      const playPromise = video.play();
      if (playPromise?.catch) playPromise.catch(() => {});
      this.updateConnector();
    }

    handleVideoError(event) {
      const video = event?.currentTarget;
      if (!this.preview?.classList.contains('is-visible') || !video?.classList.contains('is-active')) return;
      this.preview.setAttribute('data-error', 'true');
      if (this.status) this.status.textContent = 'Video could not be loaded';
    }

    updateConnector() {
      if (!this.connector || !this.connectorLine || !this.stage || !this.preview || !this.activeButton) return;

      if (this.connector.dataset.enabled !== 'true') {
        this.connector.hidden = true;
        return;
      }

      if (!this.preview.classList.contains('is-visible')) {
        this.connector.hidden = true;
        return;
      }

      this.connector.hidden = false;

      const stageRect = this.stage.getBoundingClientRect();
      const previewRect = this.preview.getBoundingClientRect();
      const buttonRect = this.activeButton.getBoundingClientRect();

      if (!stageRect.width || !stageRect.height) return;

      const x1 = ((previewRect.right - stageRect.left) / stageRect.width) * 100;
      const y1 = ((previewRect.bottom - stageRect.top) / stageRect.height) * 100;
      const x2 = ((buttonRect.left + buttonRect.width / 2 - stageRect.left) / stageRect.width) * 100;
      const y2 = ((buttonRect.top + buttonRect.height / 2 - stageRect.top) / stageRect.height) * 100;

      this.connectorLine.setAttribute('x1', String(x1));
      this.connectorLine.setAttribute('y1', String(y1));
      this.connectorLine.setAttribute('x2', String(x2));
      this.connectorLine.setAttribute('y2', String(y2));
    }
  }

  customElements.define(elementName, GhNavigatorHotspots);
})();
